// frontend/src/modules/integrations/useQuickBooksSync.ts
//
// One place for the two facts every "Push to QuickBooks" surface needs:
//   1. Is the connection usable RIGHT NOW? A row in quickbooks_connections is
//      not enough — after ~101 idle days the refresh token dies and every push
//      409s, so we gate on `connected && !reauth_required`.
//   2. Has this entity already been pushed? QuickBooks has no idempotency key,
//      so pushing twice creates two entities. The server dedupes, but the UI
//      should say so rather than letting people click hopefully.

import * as React from 'react';
import { useApi } from '../../design-system/useApi';
import type { QuickBooksStatus, QuickBooksEntityType, QuickBooksSyncResponse } from './types';

export interface QuickBooksSync {
  /** A connection exists and its token still works — safe to offer pushes. */
  canPush: boolean;
  /** Connected, but the token expired: the workspace must reconnect. */
  reauthRequired: boolean;
  /** True once this entity has a QBO id on record. */
  isPushed: (id: string) => boolean;
  /** When it was pushed, if known (ISO string from the server). */
  pushedAt: (id: string) => string | null;
  /** Record a just-completed push so the button flips without a refetch. */
  markPushed: (id: string) => void;
}

export function useQuickBooksSync(type: QuickBooksEntityType, ids: string[]): QuickBooksSync {
  const statusQ = useApi<QuickBooksStatus>('/api/integrations/quickbooks/status');
  const reauthRequired = statusQ.data?.connected === true && statusQ.data?.reauth_required === true;
  const canPush = statusQ.data?.connected === true && !reauthRequired;

  // Stable, order-independent key so a re-rendered array literal doesn't
  // retrigger the fetch on every render.
  const idKey = React.useMemo(() => Array.from(new Set(ids)).sort().join(','), [ids.join(',')]); // eslint-disable-line react-hooks/exhaustive-deps

  const syncQ = useApi<QuickBooksSyncResponse>(
    canPush && idKey ? `/api/integrations/quickbooks/sync?type=${type}&ids=${encodeURIComponent(idKey)}` : null,
    [canPush, type, idKey],
  );

  // Locally-recorded pushes layer on top of the fetched map — the sync request
  // isn't re-issued after a push, and re-fetching just to flip one label would
  // be a wasted round-trip.
  const [justPushed, setJustPushed] = React.useState<Record<string, string>>({});

  const synced = syncQ.data?.synced;
  const isPushed = React.useCallback(
    (id: string) => Boolean(synced?.[id]) || Boolean(justPushed[id]),
    [synced, justPushed],
  );
  const pushedAt = React.useCallback(
    (id: string) => justPushed[id] ?? synced?.[id]?.synced_at ?? null,
    [synced, justPushed],
  );
  const markPushed = React.useCallback((id: string) => {
    setJustPushed((prev) => ({ ...prev, [id]: new Date().toISOString() }));
  }, []);

  return { canPush, reauthRequired, isPushed, pushedAt, markPushed };
}
